/** Shared, render-only hair, headbands, and hats for gameplay and the character preview. */
import {
  type CapsuleShape,
  defaultFeel,
  makeCapsule,
  mixColor,
  NEON,
  RED,
  type RenderFeel,
  Ribbon,
  type ShapeFactory,
  type SilhouetteRenderer,
  setCapsule,
  setCapsuleTint,
} from "@lache/render";
import { Container, Graphics } from "pixi.js";
import type { Appearance } from "./appearance";

const HAIR_STRANDS = 5;
const STRAND_SEGMENTS = 9;
const CHARCOAL = 0x30313d;
const HAT = 0x343543;

function color(value: string, fallback: number): number {
  return /^#[\da-f]{6}$/i.test(value) ? Number.parseInt(value.slice(1), 16) : fallback;
}

/** A soft tapered lock. Nodes, velocities, and rounded drawing pieces are allocated once. */
class HairStrand {
  readonly root = new Container();
  private readonly outline = new Container();
  private readonly x = new Float64Array(STRAND_SEGMENTS + 1);
  private readonly y = new Float64Array(STRAND_SEGMENTS + 1);
  private readonly vx = new Float64Array(STRAND_SEGMENTS + 1);
  private readonly vy = new Float64Array(STRAND_SEGMENTS + 1);
  private readonly beforeX = new Float64Array(STRAND_SEGMENTS + 1);
  private readonly beforeY = new Float64Array(STRAND_SEGMENTS + 1);
  private readonly rims: CapsuleShape[] = [];
  private readonly pieces: CapsuleShape[] = [];
  private initialized = false;
  private length = 0.05;
  private width = 0.045;
  private count = STRAND_SEGMENTS;
  private phase = 0;
  private lastAnchorX = 0;
  private lastAnchorY = 0;

  constructor(factory: ShapeFactory, outlines: Container, fills: Container) {
    outlines.addChild(this.outline);
    fills.addChild(this.root);
    for (let i = 0; i < STRAND_SEGMENTS; i++) {
      this.rims.push(makeCapsule(factory, this.outline, NEON.ink));
      this.pieces.push(makeCapsule(factory, this.root, CHARCOAL));
    }
    this.root.visible = false;
    this.outline.visible = false;
  }

  configure(
    length: number,
    width: number,
    count: number,
    tint: number,
    phase: number,
    outlined = true,
  ): void {
    if (this.length !== length || this.count !== count || this.phase !== phase) this.initialized = false;
    this.root.visible = count > 0;
    this.outline.visible = count > 0 && outlined;
    this.count = Math.min(STRAND_SEGMENTS, Math.max(0, count));
    this.length = length;
    this.width = width;
    this.phase = phase;
    for (let i = 0; i < STRAND_SEGMENTS; i++) {
      const visible = i < this.count;
      for (const part of [this.rims[i]!, this.pieces[i]!]) {
        part.body.visible = visible;
        part.top.visible = visible;
        part.bottom.visible = visible;
      }
      setCapsuleTint(this.pieces[i]!, tint);
    }
  }

  reset(): void {
    this.initialized = false;
  }

  shiftOrigin(offset: number): void {
    for (let i = 0; i <= STRAND_SEGMENTS; i++) {
      this.x[i] = this.x[i]! - offset;
      this.beforeX[i] = this.beforeX[i]! - offset;
    }
    this.lastAnchorX -= offset;
  }

  update(
    anchorX: number,
    anchorY: number,
    time: number,
    dt: number,
    facing: number,
    velocityX: number,
  ): void {
    if (!this.root.visible || this.count === 0) return;
    const n = this.count;
    // A respawn or external pose jump must not drag old hair across the screen.
    if (!this.initialized || Math.hypot(anchorX - this.lastAnchorX, anchorY - this.lastAnchorY) > 2) {
      this.initialized = true;
      const restX = -0.2 * facing;
      const restY = -1;
      const d = Math.hypot(restX, restY);
      for (let i = 0; i <= n; i++) {
        this.x[i] = anchorX + (restX / d) * this.length * i;
        this.y[i] = anchorY + (restY / d) * this.length * i;
        this.vx[i] = 0;
        this.vy[i] = 0;
      }
      this.lastAnchorX = anchorX;
      this.lastAnchorY = anchorY;
    }
    const steps = dt > 0 ? Math.max(1, Math.ceil(Math.min(dt, 1 / 30) * 120)) : 0;
    const h = steps ? Math.min(dt, 1 / 30) / steps : 0;
    const drag = Math.exp(-5 * h);
    // A slow breeze adds a little life without making stationary hair stream horizontally.
    const wind =
      (-0.7 + 0.55 * Math.sin(time * 0.95 + this.phase) + 0.18 * Math.sin(time * 2.1)) * facing -
      Math.max(-2, Math.min(2, velocityX * 0.09));
    for (let step = 0; step < steps; step++) {
      const a = (step + 1) / steps;
      const ax = this.lastAnchorX + (anchorX - this.lastAnchorX) * a;
      const ay = this.lastAnchorY + (anchorY - this.lastAnchorY) * a;
      for (let i = 1; i <= n; i++) {
        this.beforeX[i] = this.x[i]!;
        this.beforeY[i] = this.y[i]!;
        this.vx[i] = (this.vx[i]! + wind * h) * drag;
        this.vy[i] = (this.vy[i]! - 4.5 * h) * drag;
        this.x[i] = this.x[i]! + this.vx[i]! * h;
        this.y[i] = this.y[i]! + this.vy[i]! * h;
      }
      for (let pass = 0; pass < 5; pass++) {
        this.x[0] = ax;
        this.y[0] = ay;
        for (let i = 1; i <= n; i++) {
          const dx = this.x[i]! - this.x[i - 1]!;
          const dy = this.y[i]! - this.y[i - 1]!;
          const d = Math.hypot(dx, dy) || 1e-6;
          const fraction = (d - this.length) / d;
          if (i > 1) {
            this.x[i - 1] = this.x[i - 1]! + dx * fraction * 0.5;
            this.y[i - 1] = this.y[i - 1]! + dy * fraction * 0.5;
          }
          const weight = i === 1 ? 1 : 0.5;
          this.x[i] = this.x[i]! - dx * fraction * weight;
          this.y[i] = this.y[i]! - dy * fraction * weight;
        }
      }
      for (let i = 1; i <= n; i++) {
        this.vx[i] = Math.max(-3, Math.min(3, (this.x[i]! - this.beforeX[i]!) / h));
        this.vy[i] = Math.max(-3, Math.min(3, (this.y[i]! - this.beforeY[i]!) / h));
      }
    }
    this.x[0] = anchorX;
    this.y[0] = anchorY;
    this.lastAnchorX = anchorX;
    this.lastAnchorY = anchorY;
    for (let i = 0; i < n; i++) {
      const r = this.width * (1 - (0.85 * i) / n);
      setCapsule(this.rims[i]!, this.x[i]!, this.y[i]!, this.x[i + 1]!, this.y[i + 1]!, r + 0.004);
      setCapsule(this.pieces[i]!, this.x[i]!, this.y[i]!, this.x[i + 1]!, this.y[i + 1]!, r);
    }
  }
}

/** Attach once to a figure; all accessory sprites and graphics have bounded lifetimes. */
export class CharacterAppearanceRenderer {
  private readonly hairLayer = new Container();
  private readonly hairOutlines = new Container();
  private readonly hairFills = new Container();
  private readonly tailLayer = new Container();
  private readonly headLayer = new Container();
  private readonly scalp = new Graphics();
  private readonly fringe = new Graphics();
  private readonly band = new Graphics();
  private readonly hat = new Graphics();
  private readonly strands: HairStrand[] = [];
  private readonly anchorX = new Float64Array(HAIR_STRANDS);
  private readonly anchorY = new Float64Array(HAIR_STRANDS);
  private readonly tailFeel = { ...defaultFeel, ribbonGravity: 5, ribbonDrag: 5 };
  private readonly customizedFeel = { ...defaultFeel };
  private readonly ribbonA: Ribbon;
  private readonly ribbonB: Ribbon;
  private renderedBand: Appearance["headband"] | null = null;
  private appearance: Appearance | null = null;
  private tailsDirty = true;
  private destroyed = false;
  private facing = 1;
  private lastHeadX = Number.NaN;
  private lastHeadY = Number.NaN;
  private lastHeadAngle = Number.NaN;

  constructor(
    factory: ShapeFactory,
    world: Container,
    private readonly body: SilhouetteRenderer,
    private nativeAccent = RED,
  ) {
    this.hairLayer.label = "character-hair";
    this.tailLayer.label = "character-headband-tails";
    this.headLayer.label = "character-head-details";
    this.hairLayer.addChild(this.hairOutlines, this.hairFills);
    for (let i = 0; i < HAIR_STRANDS; i++) {
      this.strands.push(new HairStrand(factory, this.hairOutlines, this.hairFills));
    }
    this.ribbonA = new Ribbon(factory, this.tailLayer, nativeAccent, 0, 0.04);
    this.ribbonB = new Ribbon(factory, this.tailLayer, nativeAccent, 0.006, 0.034);
    this.headLayer.addChild(this.scalp, this.fringe, this.band, this.hat);
    if (body.root.parent !== world) world.addChild(body.root);
    const bodyIndex = world.getChildIndex(body.root);
    world.addChildAt(this.hairLayer, bodyIndex);
    world.addChildAt(this.tailLayer, bodyIndex + 1);
    world.addChildAt(this.headLayer, bodyIndex + 3);
    this.hairLayer.visible = this.tailLayer.visible = this.headLayer.visible = false;
  }

  setAppearance(value: Appearance | null): void {
    if (this.destroyed) return;
    const previous = this.appearance;
    if (
      previous === value ||
      (previous &&
        value &&
        previous.hair === value.hair &&
        previous.hairColor === value.hairColor &&
        previous.headband === value.headband &&
        previous.headbandColor === value.headbandColor &&
        previous.hat === value.hat)
    )
      return;
    this.appearance = value ? { ...value } : null;
    this.hairLayer.visible = this.headLayer.visible = value !== null;
    if (!value) {
      this.tailLayer.visible = false;
      this.body.setAccent(this.nativeAccent);
      this.reset();
      return;
    }
    if (previous?.hair !== value.hair) for (const strand of this.strands) strand.reset();
    if (previous?.headband !== value.headband) this.tailsDirty = true;
    this.applyAppearance();
  }

  setNativeAccent(value: number): void {
    this.nativeAccent = value;
    if (!this.appearance) this.body.setAccent(value);
  }

  /** Other figures belong behind the complete character, including its trailing hair. */
  get backLayer(): Container {
    return this.hairLayer;
  }

  /** Keep native body and sash behavior, replacing only its headband and headband tails. */
  drawFeel(feel: RenderFeel): RenderFeel {
    if (!this.appearance) return feel;
    Object.assign(this.customizedFeel, feel);
    this.customizedFeel.bandWidth = 0;
    this.customizedFeel.ribbonWidth = 0;
    return this.customizedFeel;
  }

  /** Call immediately after drawing the silhouette so attachments use the same solved pose. */
  draw(feel: RenderFeel, dt: number, time: number, facing = this.body.solver.points.fb, velocityX = 0): void {
    if (!this.appearance || this.destroyed) return;
    const root = this.body.root;
    this.hairLayer.visible = this.headLayer.visible = root.visible;
    this.tailLayer.visible =
      root.visible && (this.appearance.headband === "classic" || this.appearance.headband === "long");
    if (!root.visible) return;
    const p = this.body.solver.points;
    // A paused teleport needs fresh cloth too; ordinary paused recolors keep their exact pose.
    if (
      dt <= 0 &&
      (Math.hypot(p.headX - this.lastHeadX, p.headY - this.lastHeadY) > 1e-7 ||
        Math.abs(p.headAngle - this.lastHeadAngle) > 1e-7 ||
        Math.abs(facing - this.facing) > 1e-7)
    ) {
      this.reset();
    }
    this.lastHeadX = p.headX;
    this.lastHeadY = p.headY;
    this.lastHeadAngle = p.headAngle;
    const upX = Math.sin(p.headAngle);
    const upY = Math.cos(p.headAngle);
    const forwardX = upY;
    const forwardY = -upX;
    const r = feel.headRadius;
    this.facing = Math.max(-1, Math.min(1, facing));
    const mirror = this.facing;
    // Sink, disappear, and recover in exactly the same frame as the native failure animation.
    this.hairLayer.alpha = this.tailLayer.alpha = this.headLayer.alpha = root.alpha;
    this.hairLayer.position.copyFrom(root.position);
    this.tailLayer.position.copyFrom(root.position);
    this.headLayer.position.set(p.headX + root.x, p.headY + root.y);
    this.headLayer.rotation = -p.headAngle;
    this.headLayer.scale.set(r * mirror, r);
    for (let i = 0; i < this.strands.length; i++) {
      const x = this.anchorX[i]! * r * mirror;
      const y = this.anchorY[i]! * r;
      this.strands[i]!.update(
        p.headX + forwardX * x + upX * y,
        p.headY + forwardY * x + upY * y,
        time,
        dt,
        mirror,
        velocityX,
      );
    }
    if (this.tailLayer.visible && (dt > 0 || this.tailsDirty)) {
      const x = p.headX - forwardX * mirror * r * 0.95 + upX * r * 0.25;
      const y = p.headY - forwardY * mirror * r * 0.95 + upY * r * 0.25;
      const breeze = Math.sin(time * 0.95) * 0.18;
      this.ribbonA.update(x, y + 0.008, (-0.8 + breeze) * mirror, -0.3, this.tailFeel, dt);
      this.ribbonB.update(x, y - 0.008, (-0.65 + breeze) * mirror, -0.5, this.tailFeel, dt);
      this.tailsDirty = false;
    }
  }

  reset(): void {
    for (const strand of this.strands) strand.reset();
    this.ribbonA.reset();
    this.ribbonB.reset();
    this.tailsDirty = true;
  }

  shiftOrigin(offset: number): void {
    for (const strand of this.strands) strand.shiftOrigin(offset);
    this.ribbonA.shiftOrigin(offset);
    this.ribbonB.shiftOrigin(offset);
    this.lastHeadX -= offset;
    this.tailsDirty = true;
  }

  get metadata() {
    let sceneNodes = 0;
    let graphicsInstructions = 0;
    const count = (node: Container): void => {
      sceneNodes++;
      if (node instanceof Graphics) graphicsInstructions += node.context.instructions.length;
      for (const child of node.children) count(child);
    };
    if (!this.destroyed) {
      count(this.hairLayer);
      count(this.tailLayer);
      count(this.headLayer);
    }
    return {
      equipped: this.appearance !== null && !this.destroyed,
      appearance: this.appearance ? { ...this.appearance } : null,
      headX: this.headLayer.x,
      headY: this.headLayer.y,
      headRotation: this.headLayer.rotation,
      headScaleX: this.headLayer.scale.x,
      facing: this.facing,
      bodyHeadX: this.body.solver.points.headX + this.body.root.x,
      bodyHeadY: this.body.solver.points.headY + this.body.root.y,
      bodyHeadAngle: this.body.solver.points.headAngle,
      bodyFacing: this.body.solver.points.fb,
      nativeBandWidth: this.appearance ? this.customizedFeel.bandWidth : null,
      nativeRibbonWidth: this.appearance ? this.customizedFeel.ribbonWidth : null,
      headbandVisible: this.band.visible && this.headLayer.visible,
      tailsVisible: this.tailLayer.visible,
      strandCount: this.hairLayer.visible ? this.strands.filter((strand) => strand.root.visible).length : 0,
      sceneNodes,
      graphicsInstructions,
    };
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    for (const layer of [this.hairLayer, this.tailLayer, this.headLayer]) {
      if (!layer.destroyed) layer.destroy({ children: true });
    }
  }

  private applyAppearance(): void {
    const a = this.appearance;
    if (!a) return;
    const hair = color(a.hairColor, CHARCOAL);
    const accent = color(a.headbandColor, 0xff8c1a);
    const highlight = mixColor(hair, 0xffffff, 0.3);
    const shade = mixColor(hair, NEON.ink, 0.23);
    this.body.setAccent(accent);
    this.scalp.clear();
    this.fringe.clear();
    this.band.clear();
    this.hat.clear();
    const activeStrands =
      a.hair === "ponytail" || a.hair === "mullet" ? 3 : a.hair === "long" ? HAIR_STRANDS : 0;
    for (let i = activeStrands; i < this.strands.length; i++) {
      this.strands[i]!.configure(0.04, 0.025, 0, hair, i * 0.7);
    }
    this.scalp.visible = a.hair !== "none";
    this.fringe.visible =
      a.hair !== "none" && (a.hat === "none" || (a.hair !== "mohawk" && a.hair !== "buns"));
    const cap = this.scalp;
    if (a.hair !== "none") {
      cap
        .moveTo(-1.02, -0.12)
        .bezierCurveTo(-1.3, 0.75, -0.62, 1.38, 0.15, 1.16)
        .bezierCurveTo(0.66, 1.15, 1.05, 0.74, 1.05, 0.42)
        .bezierCurveTo(0.48, 0.58, 0.06, 0.52, -0.4, 0.38)
        .lineTo(-0.68, a.hair === "crop" || a.hair === "mullet" || a.hair === "mohawk" ? 0.02 : -0.37)
        .closePath()
        .fill(a.hair === "mohawk" ? mixColor(hair, NEON.paper, 0.55) : hair)
        .stroke({ color: NEON.ink, width: 0.075 });
      cap
        .moveTo(-0.85, 0.72)
        .bezierCurveTo(-0.35, 1.12, 0.2, 1.13, 0.7, 0.78)
        .stroke({ color: highlight, width: 0.09, alpha: 0.8 });
    }
    if (a.hair === "crop" || a.hair === "mullet") {
      this.fringe
        .moveTo(-0.83, 0.57)
        .bezierCurveTo(-0.76, 1.14, -0.34, 1.37, 0.03, 1.31)
        .lineTo(-0.06, 1.1)
        .bezierCurveTo(0.17, 1.36, 0.5, 1.4, 0.76, 1.21)
        .lineTo(0.58, 1.07)
        .bezierCurveTo(0.93, 1.11, 1.14, 0.94, 1.17, 0.67)
        .lineTo(0.87, 0.48)
        .bezierCurveTo(0.2, 0.75, -0.25, 0.74, -0.83, 0.57)
        .closePath()
        .fill(hair)
        .stroke({ color: NEON.ink, width: 0.06 });
      // Broad tapered locks remain readable in the full-body phone view.
      this.fringe
        .moveTo(-0.61, 0.64)
        .bezierCurveTo(-0.59, 0.93, -0.32, 1.18, -0.08, 1.2)
        .bezierCurveTo(-0.24, 0.9, -0.29, 0.79, -0.61, 0.64)
        .closePath()
        .moveTo(-0.05, 0.72)
        .bezierCurveTo(0.15, 1.08, 0.33, 1.19, 0.58, 1.2)
        .bezierCurveTo(0.37, 0.97, 0.24, 0.81, -0.05, 0.72)
        .closePath()
        .moveTo(0.5, 0.71)
        .bezierCurveTo(0.71, 0.93, 0.9, 0.95, 1.04, 0.84)
        .bezierCurveTo(0.82, 0.82, 0.72, 0.74, 0.5, 0.71)
        .closePath()
        .fill({ color: highlight, alpha: 0.78 });
      if (a.hair === "mullet") {
        for (let i = 0; i < 3; i++) {
          this.anchorX[i] = -1.02 + i * 0.1;
          this.anchorY[i] = -0.14 + i * 0.07;
          this.strands[i]!.configure(
            0.047 - i * 0.003,
            i === 2 ? 0.018 : 0.064 - i * 0.008,
            8 - i,
            i === 2 ? highlight : hair,
            i * 0.7,
            i !== 2,
          );
        }
      }
    } else if (a.hair === "mohawk") {
      this.fringe
        .moveTo(-1.02, 0.55)
        .bezierCurveTo(-1.29, 1.05, -1.18, 1.72, -0.72, 2.02)
        .lineTo(-0.68, 1.7)
        .bezierCurveTo(-0.26, 2.23, 0.35, 2.3, 0.78, 2.06)
        .lineTo(0.39, 1.94)
        .bezierCurveTo(1.02, 1.88, 1.35, 1.38, 1.22, 0.83)
        .bezierCurveTo(0.68, 1.13, -0.2, 1.05, -1.02, 0.55)
        .closePath()
        .fill(hair)
        .stroke({ color: NEON.ink, width: 0.065 });
      this.fringe
        .moveTo(-0.88, 0.87)
        .bezierCurveTo(-0.93, 1.26, -0.83, 1.61, -0.62, 1.83)
        .moveTo(-0.44, 1)
        .bezierCurveTo(-0.42, 1.53, -0.03, 1.97, 0.42, 2.06)
        .moveTo(0.05, 1.1)
        .bezierCurveTo(0.31, 1.5, 0.67, 1.58, 0.94, 1.52)
        .moveTo(0.53, 1.06)
        .bezierCurveTo(0.74, 1.23, 0.92, 1.27, 1.05, 1.23)
        .stroke({ color: highlight, width: 0.09, alpha: 0.85 });
    } else if (a.hair === "buns") {
      // Offset the two fixed coils in profile so both remain legible around the head silhouette.
      cap
        .moveTo(-0.34, 0.42)
        .bezierCurveTo(-0.1, 0.79, -0.08, 1.01, -0.11, 1.14)
        .stroke({ color: shade, width: 0.11 });
      cap
        .moveTo(-0.91, 0.45)
        .bezierCurveTo(-0.82, 0.72, -0.65, 0.88, -0.45, 0.98)
        .moveTo(0.12, 0.55)
        .bezierCurveTo(0.46, 0.7, 0.72, 0.83, 0.85, 0.98)
        .stroke({ color: highlight, width: 0.085, alpha: 0.85 });
      this.fringe.ellipse(-0.94, 1.08, 0.63, 0.6).fill(shade).stroke({ color: NEON.ink, width: 0.075 });
      this.fringe.ellipse(0.57, 1.32, 0.55, 0.58).fill(hair).stroke({ color: NEON.ink, width: 0.075 });
      this.fringe
        .moveTo(-1.3, 0.88)
        .bezierCurveTo(-1.5, 1.42, -0.83, 1.69, -0.56, 1.27)
        .bezierCurveTo(-0.35, 0.97, -0.77, 0.64, -1.04, 0.88)
        .bezierCurveTo(-1.24, 1.04, -1.06, 1.32, -0.87, 1.19)
        .moveTo(0.17, 1.29)
        .bezierCurveTo(0.19, 1.77, 0.94, 1.85, 0.96, 1.35)
        .bezierCurveTo(0.99, 0.98, 0.45, 0.94, 0.4, 1.29)
        .bezierCurveTo(0.38, 1.53, 0.72, 1.57, 0.73, 1.37)
        .stroke({ color: highlight, width: 0.105, alpha: 0.85 });
    } else if (a.hair === "shag") {
      // Short locks belong to the head frame: they sway with the head, never in the breeze.
      cap
        .moveTo(-0.91, 0.45)
        .bezierCurveTo(-1.34, 0.16, -1.42, -0.25, -1.24, -0.67)
        .lineTo(-1.03, -0.41)
        .bezierCurveTo(-1.12, -0.68, -0.99, -0.91, -0.87, -1.04)
        .lineTo(-0.7, -0.64)
        .lineTo(-0.5, -0.91)
        .lineTo(-0.39, -0.44)
        .lineTo(-0.21, -0.64)
        .lineTo(-0.35, 0.38)
        .closePath()
        .fill(hair)
        .stroke({ color: NEON.ink, width: 0.065 });
      this.fringe
        .moveTo(-0.55, 0.87)
        .bezierCurveTo(-0.08, 1.49, 0.96, 1.28, 1.27, 0.61)
        .lineTo(1.02, 0.63)
        .lineTo(1.12, -0.12)
        .lineTo(0.77, 0.18)
        .lineTo(0.64, -0.2)
        .lineTo(0.35, 0.29)
        .lineTo(0.07, 0.02)
        .lineTo(-0.08, 0.51)
        .lineTo(-0.47, 0.3)
        .closePath()
        .fill(hair)
        .stroke({ color: NEON.ink, width: 0.06 });
      this.fringe
        .moveTo(-0.22, 0.92)
        .bezierCurveTo(0.22, 1.24, 0.71, 1.08, 0.93, 0.7)
        .moveTo(0.08, 0.83)
        .bezierCurveTo(0.33, 0.81, 0.5, 0.55, 0.55, 0.17)
        .moveTo(-0.35, 0.75)
        .bezierCurveTo(-0.22, 0.71, -0.16, 0.57, -0.1, 0.34)
        .stroke({ color: highlight, width: 0.11, alpha: 0.85 });
      cap
        .moveTo(-1.03, 0.27)
        .bezierCurveTo(-1.18, -0.08, -1.11, -0.36, -0.96, -0.56)
        .moveTo(-0.69, 0.08)
        .bezierCurveTo(-0.84, -0.26, -0.72, -0.59, -0.64, -0.63)
        .stroke({ color: highlight, width: 0.105, alpha: 0.75 });
    } else if (a.hair === "ponytail") {
      this.fringe
        .moveTo(-0.5, 0.87)
        .bezierCurveTo(0.25, 1.2, 1.1, 0.84, 1.02, 0.42)
        .bezierCurveTo(0.58, 0.52, 0.12, 0.45, -0.2, 0.33)
        .closePath()
        .fill(hair);
      this.fringe
        .moveTo(0.88, 0.64)
        .bezierCurveTo(0.3, 1.12, -0.43, 1.03, -0.92, 0.58)
        .moveTo(0.56, 0.48)
        .bezierCurveTo(0.13, 0.77, -0.42, 0.81, -0.82, 0.51)
        .stroke({ color: highlight, width: 0.085, alpha: 0.85 });
      for (let i = 0; i < 3; i++) {
        this.anchorX[i] = -1.11 - i * 0.055;
        this.anchorY[i] = 0.5 - i * 0.09;
        this.strands[i]!.configure(
          i === 2 ? 0.051 : 0.058 - i * 0.006,
          i === 2 ? 0.019 : 0.074 - i * 0.019,
          i === 2 ? 8 : 9 - i,
          i === 2 ? highlight : hair,
          i * 0.8,
          i !== 2,
        );
      }
      cap.ellipse(-1.16, 0.54, 0.32, 0.3).fill(hair).stroke({ color: NEON.ink, width: 0.065 });
      cap
        .moveTo(-1.36, 0.59)
        .bezierCurveTo(-1.22, 0.82, -0.95, 0.77, -0.93, 0.56)
        .stroke({ color: highlight, width: 0.09 });
      cap.roundRect(-1.39, 0.21, 0.4, 0.15, 0.05).fill(shade);
      cap.moveTo(-1.34, 0.31).lineTo(-1.04, 0.31).stroke({ color: highlight, width: 0.045 });
    } else if (a.hair === "long") {
      this.fringe
        .moveTo(-0.5, 0.94)
        .bezierCurveTo(0.14, 1.38, 1.02, 1.07, 1.12, 0.43)
        .bezierCurveTo(0.85, 0.74, 0.43, 0.87, 0.13, 0.84)
        .bezierCurveTo(-0.28, 0.59, -0.61, 0.26, -0.76, -0.21)
        .lineTo(-1.04, 0.24)
        .closePath()
        .fill(hair);
      this.fringe
        .moveTo(-0.14, 1.05)
        .bezierCurveTo(-0.53, 0.87, -0.83, 0.56, -0.92, 0.16)
        .moveTo(0.06, 1.09)
        .bezierCurveTo(0.52, 1.13, 0.86, 0.88, 0.96, 0.68)
        .stroke({ color: highlight, width: 0.105, alpha: 0.8 });
      this.fringe.moveTo(-0.03, 1.12).lineTo(0.1, 0.9).stroke({ color: shade, width: 0.075 });
      for (let i = 0; i < HAIR_STRANDS; i++) {
        this.anchorX[i] = -1.12 + i * 0.13;
        this.anchorY[i] = 0.35 + Math.sin(i * 0.65) * 0.17;
        this.strands[i]!.configure(
          0.071 - i * 0.003,
          i === 4 ? 0.019 : 0.079 - i * 0.008,
          9,
          i === 4 ? highlight : i === 2 ? mixColor(hair, highlight, 0.25) : hair,
          i * 0.7,
          i !== 4,
        );
      }
    }
    this.band.visible = a.headband !== "none";
    this.tailLayer.visible = a.headband === "classic" || a.headband === "long";
    if (this.renderedBand !== a.headband) {
      // Reset the fixed chains when their length changes, without replacing any sprites.
      this.ribbonA.reset();
      this.ribbonB.reset();
      this.renderedBand = a.headband;
      this.tailsDirty = true;
    }
    if (this.band.visible) {
      const width = a.headband === "sport" ? 0.47 : 0.34;
      this.band
        .roundRect(-1.045, 0.1, 2.09, width, 0.055)
        .fill(accent)
        .stroke({ color: NEON.ink, width: 0.055 });
      this.band
        .moveTo(-0.88, 0.1 + width - 0.1)
        .lineTo(0.9, 0.1 + width - 0.1)
        .stroke({ color: mixColor(accent, 0xffffff, 0.42), width: 0.045, alpha: 0.6 });
      if (a.headband === "sport") {
        this.band
          .moveTo(-0.15, 0.22)
          .lineTo(-0.01, 0.35)
          .lineTo(0.14, 0.22)
          .stroke({ color: 0xfff5de, width: 0.065 });
      }
    }
    this.ribbonA.setTint(accent);
    this.ribbonB.setTint(mixColor(accent, NEON.ink, 0.13));
    // Keep every node initialized when changing styles; only the segment length changes.
    this.tailFeel.ribbonSegmentLength = a.headband === "long" ? 0.078 : 0.036;
    this.ribbonA.segmentCount = 12;
    this.ribbonB.segmentCount = 11;
    this.hat.visible = a.hat !== "none";
    if (a.hat === "beanie") {
      this.hat
        .moveTo(-1.13, 0.42)
        .bezierCurveTo(-1.28, 1.46, -0.57, 1.62, 0.07, 1.52)
        .bezierCurveTo(0.87, 1.55, 1.18, 0.98, 1.09, 0.39)
        .closePath()
        .fill(HAT)
        .stroke({ color: NEON.ink, width: 0.08 });
      this.hat
        .roundRect(-1.17, 0.25, 2.34, 0.46, 0.12)
        .fill(mixColor(HAT, 0xffffff, 0.15))
        .stroke({ color: NEON.ink, width: 0.06 });
      this.hat.roundRect(0.26, 0.29, 0.35, 0.27, 0.035).fill(accent);
      this.hat
        .moveTo(-0.66, 0.84)
        .bezierCurveTo(-0.7, 1.23, -0.46, 1.43, -0.18, 1.44)
        .stroke({ color: 0x737483, width: 0.055, alpha: 0.6 });
    } else if (a.hat === "cap") {
      this.hat
        .moveTo(-1.11, 0.38)
        .bezierCurveTo(-1.24, 1.2, -0.6, 1.55, 0.17, 1.42)
        .bezierCurveTo(0.8, 1.38, 1.03, 0.97, 1.04, 0.48)
        .closePath()
        .fill(HAT)
        .stroke({ color: NEON.ink, width: 0.08 });
      this.hat
        .moveTo(-0.35, 0.53)
        .bezierCurveTo(0.49, 0.54, 1.55, 0.62, 2.02, 0.3)
        .bezierCurveTo(1.3, 0.21, 0.86, 0.31, 0.22, 0.3)
        .closePath()
        .fill(HAT)
        .stroke({ color: NEON.ink, width: 0.08 });
      this.hat.moveTo(-0.93, 0.54).lineTo(0.23, 0.47).stroke({ color: accent, width: 0.095 });
      this.hat.circle(0.43, 0.92, 0.16).fill(accent);
    }
  }
}
